import Axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import AuthService from "@/core/services/AuthService";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import router from "@/router";
import { App } from "vue";
import VueAxios from "vue-axios";
import axios from "axios";
import JwtService from "@/core/services/JwtService";

interface AuthRetryConfig extends AxiosRequestConfig {
  _retriedWithCurrentToken?: boolean;
  _retriedAfterRefresh?: boolean;
  rejectOnError?: boolean;
  suppressNotFoundRedirect?: boolean;
}

class ApiService {
  public static vueInstance: App;
  private static responseInterceptorInstalled = false;
  private static refreshPromise: Promise<string> | null = null;
  private static _httpRequest: AxiosInstance = Axios.create(
    ApiService.getConfig()
  );
  public static accessTokenKey = "token";

  public static async init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);

    ApiService.createRequestHandler();
    ApiService.events();

    const r = router.currentRoute.value;

    const needAuth =
      typeof r.meta.auth !== "undefined" ? !(r.meta.auth === false) : true;

    if (needAuth) {
      await AuthService.me();
    }
  }

  public static get httpRequest(): AxiosInstance {
    return ApiService._httpRequest;
  }

  public static set httpRequest(value: AxiosInstance) {
    ApiService._httpRequest = value;
  }

  public static createRequestHandler() {
    ApiService._httpRequest = Axios.create(ApiService.getConfig());
    ApiService.responseInterceptorInstalled = false;
  }

  public static getConfig() {
    const token = localStorage.getItem(ApiService.accessTokenKey);
    let config;

    if (token) {
      ApiService.config.headers.Authorization = `Bearer ${token}`;
      config = ApiService.config;
    } else {
      config = ApiService.configWithoutAuth;
    }

    return config;
  }

  public static events() {
    if (ApiService.responseInterceptorInstalled) return;
    ApiService.responseInterceptorInstalled = true;
    ApiService._httpRequest.interceptors.response.use(
      (res) => {
        return res;
      },
      async (err) => {
        if (Axios.isCancel(err)) {
          return Promise.reject(err);
        }
        // Exception
        if (typeof err.response === "undefined") {
          router.push("/500").then(() => {
            //console.error(err);
          });
          if (
            (err.config as { rejectOnError?: boolean })?.rejectOnError === true
          ) {
            return Promise.reject(err);
          }
          return Promise.reject(err);
        } else if (err.response.status === 401) {
          if (err.response.data.message === "Token has expired") {
            return ApiService.retryAfterRefresh(err);
          } else if (
            err.response.data.message ===
            "Token has expired and can no longer be refreshed"
          ) {
            ApiService.expireSession(
              ApiService.requestToken(err.config) || JwtService.getToken() || ""
            );
            return Promise.reject(err);
          } else {
            // 401 на запросе логина (неверный email/пароль) — не редиректим, пробрасываем ошибку в форму
            const isLoginRequest =
              err.config?.url === "login" ||
              String(err.config?.url || "").endsWith("/login");
            if (isLoginRequest) {
              return Promise.reject(err);
            }
            ApiService.expireSession(
              ApiService.requestToken(err.config) || JwtService.getToken() || ""
            );
            return Promise.reject(err);
          }
        } else if (
          (err.config as { rejectOnError?: boolean })?.rejectOnError === true
        ) {
          return Promise.reject(err);
        } else if (err.response.status === 412 || err.response.status === 428) {
          Swal.fire({
            text: "Ошибка запроса, обратитесь в службу поддержки. Код: 1006",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "ОК",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
        } else if (err.response.status === 413) {
          Swal.fire({
            text: "Ошибка запроса, обратитесь в службу поддержки. Код: 1005",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "ОК",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
        } else if (err.response.status === 422 || err.response.status === 429) {
          err.data = err.response.data;
          return err;
        } else if (err.response.status === 404) {
          if (
            (err.config as { suppressNotFoundRedirect?: boolean })
              ?.suppressNotFoundRedirect === true
          ) {
            return Promise.reject(err);
          }
          // redirect to 500 page
          router.push("/404").then(() => {
            console.warn("404 Server error");
          });
        } else if (err.response.status >= 500) {
          // Пробрасываем ошибку 500+ дальше для обработки в компонентах
          /*console.error(
            "Server error:",
            err.response.status,
            err.response.data
          );*/
          return Promise.reject(err);
        } else {
          console.log("TEST");
          if (typeof err.response.data !== "undefined") {
            err.data = err.response.data;
            return null;
          }
        }
      }
    );
  }

  private static async retryAfterRefresh(err) {
    const config = err.config as AuthRetryConfig;
    const currentToken = JwtService.getToken() || "";
    const failedToken = ApiService.requestToken(config);

    if (
      failedToken &&
      currentToken &&
      failedToken !== currentToken &&
      config._retriedWithCurrentToken !== true
    ) {
      if (!ApiService.samePrincipal(failedToken, currentToken)) {
        return Promise.reject(err);
      }
      config._retriedWithCurrentToken = true;
      ApiService.setRequestToken(config, currentToken);
      return ApiService._httpRequest.request(config);
    }

    if (config._retriedAfterRefresh === true) {
      ApiService.expireSession(failedToken || currentToken);
      return Promise.reject(err);
    }

    config._retriedAfterRefresh = true;
    try {
      const token = await ApiService.refreshAccessToken();
      ApiService.setRequestToken(config, token);
      return ApiService._httpRequest.request(config);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }

  private static refreshAccessToken(): Promise<string> {
    if (ApiService.refreshPromise !== null) {
      return ApiService.refreshPromise;
    }

    const refreshToken = JwtService.getToken() || "";
    const request: Promise<string> = Axios.post(
      process.env.VUE_APP_API_URL + "refresh",
      {},
      { headers: { Authorization: `Bearer ${refreshToken}` } }
    )
      .then((response) => {
        const currentToken = JwtService.getToken() || "";
        if (currentToken !== refreshToken) {
          if (
            currentToken &&
            ApiService.samePrincipal(refreshToken, currentToken)
          ) {
            return currentToken;
          }
          throw new Error("Авторизация была завершена.");
        }

        const accessToken = response.data?.access_token;
        if (typeof accessToken !== "string" || accessToken.length === 0) {
          throw new Error("Сервис авторизации не вернул access token.");
        }

        JwtService.saveToken(accessToken);
        ApiService.createRequestHandler();
        ApiService.events();
        return accessToken;
      })
      .catch((error) => {
        const currentToken = JwtService.getToken() || "";
        if (currentToken !== refreshToken) {
          if (
            currentToken &&
            ApiService.samePrincipal(refreshToken, currentToken)
          ) {
            return currentToken;
          }
          return Promise.reject(error);
        }

        if (
          error.response?.status === 405 &&
          error.response?.data?.data?.type === "expired"
        ) {
          void Swal.fire({
            text: "Ошибка авторизации!",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "ОК",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
        }
        ApiService.expireSession(refreshToken);
        return Promise.reject(error);
      })
      .finally(() => {
        if (ApiService.refreshPromise === request) {
          ApiService.refreshPromise = null;
        }
      });
    ApiService.refreshPromise = request;
    return request;
  }

  private static requestToken(config?: AxiosRequestConfig): string {
    const authorization = String(
      (config?.headers as { Authorization?: string } | undefined)
        ?.Authorization || ""
    );
    return authorization.startsWith("Bearer ")
      ? authorization.substring("Bearer ".length)
      : "";
  }

  private static setRequestToken(
    config: AxiosRequestConfig,
    token: string
  ): void {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }

  private static samePrincipal(leftToken: string, rightToken: string): boolean {
    const left = ApiService.tokenSubject(leftToken);
    const right = ApiService.tokenSubject(rightToken);
    return left !== null && right !== null && left === right;
  }

  private static tokenSubject(token: string): string | null {
    try {
      const encoded = token.split(".")[1];
      if (!encoded) return null;
      const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        "="
      );
      const subject = JSON.parse(window.atob(padded))?.sub;
      return typeof subject === "string" || typeof subject === "number"
        ? String(subject)
        : null;
    } catch {
      return null;
    }
  }

  private static expireSession(failedToken: string): void {
    if ((JwtService.getToken() || "") !== failedToken) return;

    ApiService.unsetToken();
    ApiService.createRequestHandler();
    ApiService.events();
    if (router.currentRoute.value.name !== "sign-in") {
      void router.push({ name: "sign-in" });
    }
  }

  public static unsetToken() {
    localStorage.removeItem(ApiService.accessTokenKey);
    localStorage.removeItem("email");
  }

  public static config = {
    baseURL: process.env.VUE_APP_API_URL,
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      Authorization: "",
    },
  };

  public static configWithoutAuth = {
    baseURL: process.env.VUE_APP_API_URL,
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  };
}

export default ApiService;
